// The three JSON fields of a publish template, typed. The shapes are what the
// Play runner reads (runner/play/src/scenarios/{create-app,app-content,
// content-rating,store-settings,upload-first-aab}.mjs); the validation here
// mirrors the runner's own `validate` functions so a template saved from the
// form never comes back as a `manual` block for a shape reason.

// ---------------------------------------------------------------------------
// App content (create_app + the eight declarations)

export type AppType = 'app' | 'game';
export type Pricing = 'free' | 'paid';
export type AppAccess = 'unrestricted' | 'restricted';

export const AGE_BANDS = ['0-5', '6-8', '9-12', '13-15', '16-17', '18+'] as const;
export type AgeBand = typeof AGE_BANDS[number];

export const AD_ID_PURPOSES = [
  { id: 'app_functionality', label: 'App functionality' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'developer_communications', label: 'Developer communications' },
  { id: 'advertising', label: 'Advertising or marketing' },
  { id: 'fraud_prevention', label: 'Fraud prevention, security, and compliance' },
  { id: 'personalization', label: 'Personalization' },
  { id: 'account_management', label: 'Account management' },
] as const;
export type AdIdPurpose = typeof AD_ID_PURPOSES[number]['id'];

/** Choice ids the console renders as POLICY_RESPONSE_CHOICE_ID_<id>. Only the
 *  "none" answers are captured by the runner; anything else is typed in. */
export const FINANCE_NONE = 'FINANCE_NONE';
export const HEALTH_NONE = 'NOT_HEALTH_APP';

export interface AppContent {
  app_type: AppType;
  pricing: Pricing;
  accept_developer_policy: true;
  accept_play_app_signing: true;
  accept_export_laws: true;
  contains_ads: boolean;
  app_access: AppAccess;
  target_age_groups: AgeBand[];
  government_app: boolean;
  financial_features: string[];
  health_categories: string[];
  advertising_id: { used: boolean; purposes: AdIdPurpose[] };
  /** The News apps declaration. The console asks it of apps, not games. */
  news_app: boolean;
}

export function defaultAppContent(): AppContent {
  return {
    app_type: 'game',
    pricing: 'free',
    accept_developer_policy: true,
    accept_play_app_signing: true,
    accept_export_laws: true,
    contains_ads: true,
    app_access: 'unrestricted',
    target_age_groups: ['18+'],
    government_app: false,
    financial_features: [FINANCE_NONE],
    health_categories: [HEALTH_NONE],
    advertising_id: { used: true, purposes: ['analytics', 'advertising'] },
    news_app: false,
  };
}

// ---------------------------------------------------------------------------
// Content rating (IARC). Two of the console's three questionnaires are
// captured: "Game" and "All other app types". The second is a questionnaire
// of its own, answered "no" throughout by the runner; `answers` belongs to the
// Game one and is not read for it.

export const IARC_CATEGORIES = [
  { id: 'violence', label: 'Violence' },
  { id: 'fear', label: 'Fear' },
  { id: 'sexuality', label: 'Sexuality' },
  { id: 'language', label: 'Language' },
  { id: 'controlled_substances', label: 'Controlled substances' },
  { id: 'crude_humor', label: 'Crude humor' },
  { id: 'digital_purchases', label: 'Digital purchases' },
] as const;
export type IarcCategory = typeof IARC_CATEGORIES[number]['id'];

export const GAMBLING_KINDS = [
  { id: 'themes', label: 'Gambling themes or imagery' },
  { id: 'lotteries', label: 'Lotteries, raffles or prize draws' },
  { id: 'in_game_currency', label: 'Simulated gambling with in-game currency' },
  { id: 'real_money', label: 'Real-money gambling' },
] as const;
export type GamblingKind = typeof GAMBLING_KINDS[number]['id'];

export const IARC_OTHER = [
  { id: 'user_interaction', label: 'Users can interact or exchange content' },
  { id: 'shares_location', label: 'Shares the user’s location with others' },
  { id: 'nazi_symbols', label: 'Nazi symbols or references' },
  { id: 'korea_harm', label: 'Content that could be harmful in Korea' },
  { id: 'terrorism_glorified', label: 'Glorification of terrorism' },
  { id: 'crime_techniques', label: 'Detailed instructions for crimes' },
] as const;
export type IarcOther = typeof IARC_OTHER[number]['id'];

/** "All other app types", in the console's order (captured October 2026).
 *  The runner answers every one of them "no". */
export const IARC_OTHER_APP_QUESTIONS = [
  { card: 'Downloaded app', question: 'Ratings-relevant content (sex, violence, language) in the app package' },
  { card: 'User content sharing', question: 'Users interact or exchange content (voice, text, images, audio)' },
  { card: 'Online content', question: 'Content accessed from the app that is not part of the download (movies, products, songs, AI content, news)' },
  { card: 'Age-restricted products', question: 'Promotes or sells cigarettes, alcohol, firearms, gambling and the like' },
  { card: 'Miscellaneous', question: 'Shares the user’s precise location with other users' },
  { card: 'Miscellaneous', question: 'Users can purchase digital goods' },
  { card: 'Miscellaneous', question: 'Cash rewards, gift cards, play-to-earn, crypto rewards or NFTs' },
  { card: 'Miscellaneous', question: 'A web browser or search engine' },
  { card: 'Miscellaneous', question: 'Primarily a news or educational product' },
] as const;

/** The questionnaire picked on the category step; it follows the app type. */
export type IarcAppCategory = 'game' | 'other';
export const iarcCategoryFor = (t: AppType): IarcAppCategory => (t === 'game' ? 'game' : 'other');

export interface ContentRating {
  category: IarcAppCategory;
  answers: Record<IarcCategory, boolean> & {
    gambling: { present: boolean; kinds: GamblingKind[]; central: boolean };
    other: Record<IarcOther, boolean>;
  };
}

export function defaultContentRating(): ContentRating {
  return {
    category: 'game',
    answers: {
      violence: false, fear: false, sexuality: false, language: false,
      controlled_substances: false, crude_humor: false, digital_purchases: false,
      gambling: { present: false, kinds: [], central: false },
      other: {
        user_interaction: false, shares_location: false, nazi_symbols: false,
        korea_harm: false, terrorism_glorified: false, crime_techniques: false,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Listing defaults (store settings + first release notes)

export interface ListingDefaults {
  category: string;
  website: string;
  phone: string;
  release_notes: string;
}

export function defaultListingDefaults(): ListingDefaults {
  return { category: 'GAME_CASINO', website: '', phone: '', release_notes: 'Initial release.' };
}

export interface PlayCategory { id: string; label: string; type: AppType }

/** Play Console store categories, with the label the console's dropdown shows
 *  (the runner picks by label, so these have to match the console). */
export const PLAY_CATEGORIES: PlayCategory[] = [
  ...[
    ['GAME_ACTION', 'Action'], ['GAME_ADVENTURE', 'Adventure'], ['GAME_ARCADE', 'Arcade'], ['GAME_BOARD', 'Board'],
    ['GAME_CARD', 'Card'], ['GAME_CASINO', 'Casino'], ['GAME_CASUAL', 'Casual'], ['GAME_EDUCATIONAL', 'Educational'],
    ['GAME_MUSIC', 'Music'], ['GAME_PUZZLE', 'Puzzle'], ['GAME_RACING', 'Racing'], ['GAME_ROLE_PLAYING', 'Role Playing'],
    ['GAME_SIMULATION', 'Simulation'], ['GAME_SPORTS', 'Sports'], ['GAME_STRATEGY', 'Strategy'], ['GAME_TRIVIA', 'Trivia'],
    ['GAME_WORD', 'Word'],
  ].map(([id, label]) => ({ id: id!, label: label!, type: 'game' as const })),
  ...[
    ['ART_AND_DESIGN', 'Art & Design'], ['AUTO_AND_VEHICLES', 'Auto & Vehicles'], ['BEAUTY', 'Beauty'],
    ['BOOKS_AND_REFERENCE', 'Books & Reference'], ['BUSINESS', 'Business'], ['COMICS', 'Comics'],
    ['COMMUNICATION', 'Communication'], ['DATING', 'Dating'], ['EDUCATION', 'Education'], ['ENTERTAINMENT', 'Entertainment'],
    ['EVENTS', 'Events'], ['FINANCE', 'Finance'], ['FOOD_AND_DRINK', 'Food & Drink'], ['HEALTH_AND_FITNESS', 'Health & Fitness'],
    ['HOUSE_AND_HOME', 'House & Home'], ['LIBRARIES_AND_DEMO', 'Libraries & Demo'], ['LIFESTYLE', 'Lifestyle'],
    ['MAPS_AND_NAVIGATION', 'Maps & Navigation'], ['MEDICAL', 'Medical'], ['MUSIC_AND_AUDIO', 'Music & Audio'],
    ['NEWS_AND_MAGAZINES', 'News & Magazines'], ['PARENTING', 'Parenting'], ['PERSONALIZATION', 'Personalization'],
    ['PHOTOGRAPHY', 'Photography'], ['PRODUCTIVITY', 'Productivity'], ['SHOPPING', 'Shopping'], ['SOCIAL', 'Social'],
    ['SPORTS', 'Sports'], ['TOOLS', 'Tools'], ['TRAVEL_AND_LOCAL', 'Travel & Local'],
    ['VIDEO_PLAYERS', 'Video Players & Editors'], ['WEATHER', 'Weather'],
  ].map(([id, label]) => ({ id: id!, label: label!, type: 'app' as const })),
];

// ---------------------------------------------------------------------------
// Reading stored JSON. Tolerant: a template written by hand before the form
// existed may miss keys or hold odd values; each field falls back to its
// default so the form always opens, and validation says what is off.

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const bool = (v: unknown, dflt: boolean) => (typeof v === 'boolean' ? v : dflt);
const str = (v: unknown, dflt = '') => (typeof v === 'string' ? v : dflt);
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], dflt: T): T =>
  (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : dflt);

function parseJson(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw);
    return isObj(v) ? v : {};
  } catch {
    return {};
  }
}

export function readAppContent(raw: string | null | undefined): AppContent {
  const j = parseJson(raw);
  const d = defaultAppContent();
  const adId = isObj(j.advertising_id) ? j.advertising_id : {};
  const purposeIds = AD_ID_PURPOSES.map((p) => p.id) as readonly string[];
  const ages = strList(j.target_age_groups).filter((g): g is AgeBand => (AGE_BANDS as readonly string[]).includes(g));
  return {
    app_type: oneOf(j.app_type, ['app', 'game'], d.app_type),
    pricing: oneOf(j.pricing, ['free', 'paid'], d.pricing),
    accept_developer_policy: true,
    accept_play_app_signing: true,
    accept_export_laws: true,
    contains_ads: bool(j.contains_ads, d.contains_ads),
    app_access: oneOf(j.app_access, ['unrestricted', 'restricted'], d.app_access),
    target_age_groups: ages.length ? ages : d.target_age_groups,
    government_app: bool(j.government_app, d.government_app),
    financial_features: j.financial_features === undefined ? d.financial_features : strList(j.financial_features),
    health_categories: j.health_categories === undefined ? d.health_categories : strList(j.health_categories),
    advertising_id: {
      used: bool(adId.used, d.advertising_id.used),
      purposes: adId.purposes === undefined
        ? d.advertising_id.purposes
        : strList(adId.purposes).filter((p): p is AdIdPurpose => purposeIds.includes(p)),
    },
    news_app: bool(j.news_app, d.news_app),
  };
}

export function readContentRating(raw: string | null | undefined): ContentRating {
  const j = parseJson(raw);
  const d = defaultContentRating();
  const a = isObj(j.answers) ? j.answers : {};
  const g = isObj(a.gambling) ? a.gambling : {};
  const o = isObj(a.other) ? a.other : {};
  const kindIds = GAMBLING_KINDS.map((k) => k.id) as readonly string[];
  const out = d;
  out.category = oneOf(j.category, ['game', 'other'], d.category);
  for (const c of IARC_CATEGORIES) out.answers[c.id] = bool(a[c.id], false);
  out.answers.gambling = {
    present: bool(g.present, false),
    kinds: strList(g.kinds).filter((k): k is GamblingKind => kindIds.includes(k)),
    central: bool(g.central, false),
  };
  for (const c of IARC_OTHER) out.answers.other[c.id] = bool(o[c.id], false);
  return out;
}

export function readListingDefaults(raw: string | null | undefined): ListingDefaults {
  const j = parseJson(raw);
  const d = defaultListingDefaults();
  return {
    category: str(j.category, d.category).toUpperCase(),
    website: str(j.website),
    phone: str(j.phone),
    release_notes: str(j.release_notes, d.release_notes),
  };
}

// ---------------------------------------------------------------------------
// Validation: what the runner would refuse, said before saving.

export interface TemplateProblem { section: 'app_content' | 'content_rating' | 'listing' ; message: string }

export function validateAppContent(a: AppContent): string[] {
  const e: string[] = [];
  if (a.app_access !== 'unrestricted') e.push('Restricted app access needs test credentials a human has to supply; the runner will hand the job over.');
  if (a.target_age_groups.length !== 1 || a.target_age_groups[0] !== '18+') e.push('Only an 18+ audience is automated; any child age band walks a wizard the runner has not captured.');
  if (a.government_app) e.push('A government app declaration needs details a human has to enter.');
  if (a.financial_features.length === 0) e.push('Financial features: pick "none" or enter at least one choice id.');
  if (a.health_categories.length === 0) e.push('Health: pick "none" or enter at least one choice id.');
  if (a.advertising_id.used && a.advertising_id.purposes.length === 0) e.push('Advertising ID: pick at least one purpose, or say it is not used.');
  if (a.app_type === 'app' && a.news_app) e.push('A news app declaration needs publisher details a human has to enter.');
  return e;
}

export function validateContentRating(r: ContentRating, appType: AppType): string[] {
  const e: string[] = [];
  if (r.category !== iarcCategoryFor(appType)) {
    e.push(appType === 'game'
      ? 'A game is rated with the Game questionnaire.'
      : 'An app is rated with the "All other app types" questionnaire, not the Game one.');
  }
  // "All other app types" is answered "no" throughout; the Game answers below
  // are not read for it.
  if (r.category === 'other') return e;
  for (const c of IARC_CATEGORIES) {
    if (r.answers[c.id]) e.push(`${c.label}: a "yes" opens follow-up questions the runner has not captured; it would hand the job over.`);
  }
  const g = r.answers.gambling;
  if (g.present) {
    if (g.kinds.length === 0) e.push('Gambling: pick at least one kind.');
    if (g.kinds.includes('real_money')) e.push('Real-money gambling is a policy matter a human has to handle.');
  }
  for (const c of IARC_OTHER) {
    if (r.answers.other[c.id]) e.push(`${c.label}: a "yes" is not captured by the runner.`);
  }
  return e;
}

export function validateListingDefaults(l: ListingDefaults, appType: AppType): string[] {
  const e: string[] = [];
  const cat = PLAY_CATEGORIES.find((c) => c.id === l.category);
  if (!l.category) e.push('Store category is required.');
  else if (cat && cat.type !== appType) e.push(`Category "${cat.label}" is a${appType === 'game' ? 'n app' : ' game'} category, but the app type is ${appType}.`);
  if (l.website.trim() && !/^https?:\/\//i.test(l.website.trim())) e.push('Website must start with http:// or https://.');
  if (l.release_notes.length > 500) e.push('Release notes are limited to 500 characters.');
  return e;
}

/** JSON exactly as the runner expects it, for saving. */
export function serializeListingDefaults(l: ListingDefaults): Record<string, unknown> {
  const out: Record<string, unknown> = { category: l.category };
  if (l.website.trim()) out.website = l.website.trim();
  if (l.phone.trim()) out.phone = l.phone.trim();
  if (l.release_notes.trim()) out.release_notes = l.release_notes.trim();
  return out;
}

/** One line for the template list: what kind of app these answers describe. */
export function describeTemplate(t: { app_content: string; content_rating: string; listing_defaults: string }): string {
  const a = readAppContent(t.app_content);
  const r = readContentRating(t.content_rating);
  const l = readListingDefaults(t.listing_defaults);
  const cat = PLAY_CATEGORIES.find((c) => c.id === l.category)?.label ?? l.category;
  const parts = [`${cat} ${a.app_type}`, a.pricing, a.contains_ads ? 'ads' : 'no ads', a.target_age_groups.join('/')];
  if (r.answers.gambling.present) parts.push(r.answers.gambling.kinds.includes('in_game_currency') ? 'simulated gambling' : 'gambling themes');
  return parts.join(' · ');
}
